import {useEffect, useRef} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {type Thread, useAgentChatStore} from './store';
import {
  clearUnseenSettledThread,
  getReadStatusSnapshot,
  recordUnseenSettledThread,
  subscribeReadStatus,
  type UnseenSettledThread,
} from './threadReadStatus';

function runningIds(threads: Record<string, Thread>): Set<string> {
  const ids = new Set<string>();
  for (const id in threads) {
    if (threads[id].status === 'running') {
      ids.add(id);
    }
  }
  return ids;
}

/**
 * useUnseenSettledThread tracks the most recently settled thread (running →
 * idle/error) that the user hasn't seen.
 *
 * Records a transition only while the chat is closed (`open === false`).
 *
 * The returned value (persisted, also updated on reload/cross-tab) drives the
 * durable unread dot. `onLiveSettled` fires only for transitions observed live
 * in this tab/session, so callers can show an ephemeral toast without it
 * re-firing on a rehydrated value after a page reload.
 */
export function useUnseenSettledThread(
  open: boolean,
  onLiveSettled?: (thread: UnseenSettledThread) => void,
  onSeen?: (thread: UnseenSettledThread) => void
): UnseenSettledThread | null {
  const store = useAgentChatStore();
  const snapshot = useSyncExternalStore(
    subscribeReadStatus,
    getReadStatusSnapshot
  );

  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;
  // Trigger onSeen when a previously unseen thread is opened.
  // This enables synced toast dismissal, e.g., thread opened via
  // the ARIA trigger button (as opposed to "Go to chat") or in another tab
  const prevUnseenSettledThreadRef = useRef(snapshot.unseenSettledThread);
  useEffect(() => {
    const prev = prevUnseenSettledThreadRef.current;
    const current = snapshot.unseenSettledThread;
    prevUnseenSettledThreadRef.current = current;
    if (prev != null && prev.rootTurnId !== current?.rootTurnId) {
      onSeenRef.current?.(prev);
    }
  }, [snapshot.unseenSettledThread]);

  // Read the latest `open`/`onLiveSettled` inside the subscription without
  // re-subscribing, as that would reset the prev-status baseline and
  // miss transitions.
  const openRef = useRef(open);
  openRef.current = open;
  const onLiveSettledRef = useRef(onLiveSettled);
  onLiveSettledRef.current = onLiveSettled;

  // Opening the chat means the user has seen the current state.
  useEffect(() => {
    if (open) {
      clearUnseenSettledThread();
    }
  }, [open]);

  useEffect(() => {
    // Baseline currently-running threads so the first diff doesn't treat
    // already-settled threads as fresh completions. Tracking only running ids
    // (usually 0–1) keeps the per-dispatch work small during streaming.
    let prevRunning = runningIds(store.getState().threads);

    return store.subscribe(() => {
      const {threads} = store.getState();

      if (!openRef.current) {
        for (const id of prevRunning) {
          const thread = threads[id];

          if (
            thread != null &&
            thread.status !== 'running' &&
            thread.rootTurnId != null
          ) {
            const settled = {rootTurnId: thread.rootTurnId};
            // Persisted slot backs the durable dot...
            recordUnseenSettledThread(settled);
            // ...while the callback fires only for this live completion (toast).
            onLiveSettledRef.current?.(settled);
          }
        }
      }

      prevRunning = runningIds(threads);
    });
  }, [store]);

  return snapshot.unseenSettledThread;
}
